import {PercentPipe} from '@angular/common';
import {HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  inject,
  Input,
  resource,
  signal,
  Signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {MatButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatTableModule} from '@angular/material/table';
import {MatTooltip} from '@angular/material/tooltip';
import {DeleteDialogComponent} from '@app/dialogs/object-delete-dialog/object-delete-dialog.component';
import {AuthenticationService} from '@authentication/authentication.service';
import {
  DatasetFileVersionResponse,
  DatasetResponse,
  FileVersionResponse,
  PresignedUrlResponse,
  ProjectFileVersionResponse,
  ProjectResponse,
} from '@backend';
import {IndexService} from '@middleware';
import {CommonsSearchDatasetFlagsOnly, CommonsSearchProjectFlagsOnly} from '@models/commons-types';
import {AttestationService} from '@services/attestation/attestation.service';
import {LandingServiceConfigService} from '@services/config/landing-service-config.service';
import {LandingServiceBrokerService} from '@services/landing-service-broker/landing-service-broker.service';
import {SensitivityLevel} from '@shared/labels/sensitivity-level';
import {formatSize} from '@utilities/format-size';
import {lastValueFrom, Observable} from 'rxjs';

const HSD_DOWNLOAD_ATTESTATION_TITLE = 'Download highly-sensitive dataset';
const NON_HSD_DOWNLOAD_ATTESTATION_TITLE = 'Download dataset';

const PROJECT_DOCUMENT_COLUMNS = ['file_name', 'file_size', 'category', 'time_created', 'uploaded_by'];
const DATASET_FILE_VERSION_COLUMNS = [
  'status_latest',
  'file_name',
  'file_size',
  'time_created',
  'uploaded_by',
  'hipaa_status',
];

@Component({
  selector: 'app-file-table',
  standalone: true,
  imports: [MatButton, MatIcon, MatTableModule, MatFormField, MatLabel, MatSelect, MatOption, PercentPipe, MatTooltip],
  templateUrl: './file-table.component.html',
  styleUrl: './file-table.component.scss',
})
export class FileTableComponent {
  @Input() tablename: string;
  @Input() container!: Signal<ProjectResponse | DatasetResponse>;
  @Input() flags: (CommonsSearchProjectFlagsOnly | CommonsSearchDatasetFlagsOnly) & {
    _can_download_latest_data?: boolean;
    _can_download_all_data?: boolean;
    _can_download_data?: boolean;
  };
  isProject(): boolean {
    return this.container && 'project_file_versions' in this.container();
  }
  project: Signal<ProjectResponse> = computed(() =>
    this.isProject() ? (this.container() as ProjectResponse) : undefined,
  );
  dataset: Signal<DatasetResponse> = computed(() =>
    !this.isProject() ? (this.container() as DatasetResponse) : undefined,
  );
  files: Signal<((ProjectFileVersionResponse | DatasetFileVersionResponse) & FileVersionResponse)[]> = computed(() => {
    if (!this.container?.()) return [];
    if (this.isProject()) {
      return (this.container() as ProjectResponse).project_file_versions
        .map(pfv => ({...pfv, ...pfv.file_version}) as ProjectFileVersionResponse & FileVersionResponse)
        .filter(pfv => pfv.is_latest_and_not_deleted)
        .sort((a, b) => Date.parse(b.time_created) - Date.parse(a.time_created));
    } else {
      return (this.container() as DatasetResponse).dataset_file_versions
        .map(dfv => ({...dfv, ...dfv.file_version}) as DatasetFileVersionResponse & FileVersionResponse)
        .filter(dfv => !dfv.scheduled_for_deletion_on)
        .sort((a, b) => Date.parse(b.time_created) - Date.parse(a.time_created));
    }
  });
  filesObs = toObservable(this.files);

  downloadFunc: (file: FileVersionResponse) => Observable<PresignedUrlResponse>;
  uploadEndpoint: () => string;
  deleteFunc: (file: FileVersionResponse) => Observable<void>;
  syncFunc: () => Observable<void>;
  show_upload_ui = signal(false);
  isUploading = signal(false);
  uploadProgress = signal(0);
  newFile: WritableSignal<File> = signal(undefined);
  hsd_download_attestation: Signal<TemplateRef<undefined>> = viewChild.required('hsd_download_attestation');
  non_hsd_download_attestation: Signal<TemplateRef<undefined>> = viewChild.required('non_hsd_download_attestation');

  _lsbs = inject(LandingServiceBrokerService);
  _lscs = inject(LandingServiceConfigService);
  _org = computed(() => this.container?.()?.storage_organization?.name);
  _services = this._lsbs.getResource(this._org);
  _config = this._lscs.getResource(this._org);
  file_size_limit = computed(() => this._config.value()?.file_size_limit);
  allow_download = computed(
    () =>
      this._config.value()?.allow_hsd_data_download !== false ||
      this.isProject() ||
      this.dataset().sensitivity_level.id !== SensitivityLevel.HSD,
  );
  categories = resource({
    //TODO: Add `chain` support when Angular v22 hits
    params: () => {
      if (this._services.hasValue()) {
        return this._services.value().LabelsService;
      }
      return undefined;
    },
    loader: async ({params: IndexService}) =>
      await lastValueFrom(IndexService.getLabelTypesByName('project-file-category')),
    defaultValue: [],
  });

  constructor(
    indexService: IndexService,
    private readonly snackbarService: MatSnackBar,
    private readonly dialogService: MatDialog,
    private readonly authService: AuthenticationService,
    private readonly attestationService: AttestationService,
  ) {
    effect(() => {
      if (this._services.hasValue()) {
        const services = this._services.value();
        if (this.isProject()) {
          this.downloadFunc = (file: FileVersionResponse) =>
            services.ProjectsService.generateProjectFileDownloadLink(this.container().id, file.id);
          this.uploadEndpoint = () => `${services.ProjectsService._baseUrl}/projects/${this.container().id}/files`;
          this.deleteFunc = (file: FileVersionResponse) =>
            services.ProjectsService.deleteProjectFile(this.container().id, file.id, {immediate: false});
          this.syncFunc = () =>
            indexService.syncProjectInIndex(this.container().id, {
              storage_organization: this.container().storage_organization.name,
            });
        } else {
          this.downloadFunc = (file: FileVersionResponse) =>
            services.DatasetsService.generateDatasetFileDownloadLink(this.container().id, file.id);
          this.uploadEndpoint = () => `${services.DatasetsService._baseUrl}/datasets/${this.container().id}/files`;
          this.deleteFunc = (file: FileVersionResponse) =>
            services.DatasetsService.deleteDatasetFile(this.container().id, file.id, {immediate: false});
          this.syncFunc = () =>
            indexService.syncDatasetInIndex(this.container().id, {
              storage_organization: this.container().storage_organization.name,
            });
          this._download_dsp = (id: string) =>
            services.ProjectsService.generateProjectFileDownloadLink(
              this.dataset().associated_projects.find(p => p.is_primary_project).project.id,
              id,
            );
        }
      }
    });
  }

  getColumns() {
    if (this.container?.()) {
      let columns: string[];
      if (this.isProject()) {
        columns = [...PROJECT_DOCUMENT_COLUMNS];
      } else {
        columns = [...DATASET_FILE_VERSION_COLUMNS];
      }
      if (
        (this.flags._can_download_data || this.flags._can_download_latest_data || this.flags._can_download_all_data) &&
        this.allow_download()
      ) {
        columns.push('action_download');
      }
      if (this.flags._can_delete_data) {
        columns.push('action_delete');
      }
      return columns;
    }
    return [];
  }

  get_file_size(file: FileVersionResponse) {
    return formatSize(file.file_size);
  }

  get_uploaded_by(file: FileVersionResponse) {
    return file.uploaded_by.display_name ?? file.uploaded_by.email;
  }

  _get_document_type(file: ProjectFileVersionResponse & FileVersionResponse) {
    return this.categories.value()?.find(cat => cat.id == file.category)?.text ?? file.category;
  }

  get_document_type = this._get_document_type.bind(this);

  get_date_created(file: FileVersionResponse) {
    let dtf = new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    });
    return dtf.format(Date.parse(file.time_created));
  }

  getErrorDetails(ex: unknown): string {
    return (ex as any)?.error?.detail ?? (ex as any)?.message ?? String(ex);
  }

  async onDownload(file: FileVersionResponse, options?: {harmonizeDates?: boolean}) {
    options ??= {};
    if (!this.isProject()) {
      let isHSD = this.dataset().sensitivity_level.id === SensitivityLevel.HSD;
      if (
        !(await this.attestationService.confirmWithCancel(
          isHSD ? this.hsd_download_attestation() : this.non_hsd_download_attestation(),
          isHSD ? HSD_DOWNLOAD_ATTESTATION_TITLE : NON_HSD_DOWNLOAD_ATTESTATION_TITLE,
        ))
      )
        return;
    }
    let link;
    try {
      link = (await lastValueFrom(this.downloadFunc(file))).download_url;
    } catch (ex) {
      this.snackbarService.open(`Couldn't download ${file.file_name}: ${this.getErrorDetails(ex)}`, undefined, {
        duration: 15000,
        panelClass: 'snackbar-error',
      });
    }
    if (link) {
      if (options.harmonizeDates) {
        link += '&harmonize_dates=true';
      }
      const a = document.createElement('a');
      a['href'] = link;
      a['download'] = file.file_name;
      a.click();
    }
  }

  async onDelete(file: FileVersionResponse) {
    if (
      (await lastValueFrom(
        this.dialogService
          .open(DeleteDialogComponent, {data: {objectName: file.file_name, objectKind: 'file'}})
          .afterClosed(),
      )) === true
    ) {
      try {
        await lastValueFrom(this.deleteFunc(file));
        await lastValueFrom(this.syncFunc());
        //TODO: how to invalidate `container` so the world reloads? Force page reload I guess...
        window.location.reload();
      } catch (ex) {
        this.snackbarService.open(`Couldn't delete ${file.file_name}: ${this.getErrorDetails(ex)}`, undefined, {
          duration: 15000,
          panelClass: 'snackbar-error',
        });
      }
    }
  }

  async onUpload(categoryField: MatSelect) {
    if (this.hipaaScanInProgress()) return;
    if (!this._config.hasValue() || !this.newFile()) return;
    const excess = this.newFile().size - this.file_size_limit();
    if (excess > 0) {
      this.snackbarService.open(
        `Couldn't upload ${this.newFile().name}: This file exceeds the ${formatSize(this.file_size_limit())} limit by ${formatSize(excess)}.`,
        undefined,
        {
          duration: 5000,
          panelClass: 'snackbar-error',
        },
      );
      return;
    }
    this.isUploading.set(true);
    this.uploadProgress.set(0);
    try {
      let endpoint = this.uploadEndpoint();
      if (this.isProject()) {
        endpoint += '?document_type=' + categoryField.value;
      }
      const [xhr, xhrPromise] = this.XHR();
      xhr.upload.addEventListener('progress', ev =>
        this.uploadProgress.set(ev.lengthComputable ? ev.loaded / ev.total : 0),
      );
      xhr.open('POST', endpoint, true);
      xhr.responseType = 'json';
      xhr.setRequestHeader('Authorization', `Bearer ${this.authService.token()}`);
      const formData = new FormData();
      formData.append('file', this.newFile());
      xhr.send(formData);
      await xhrPromise;
      this.uploadProgress.set(1);
      await lastValueFrom(this.syncFunc());
      //TODO: how to invalidate `container` so the world reloads? Force page reload I guess...
      window.location.reload();
    } catch (ex) {
      this.snackbarService.open(`Couldn't upload ${this.newFile().name}: ${this.getErrorDetails(ex)}`, undefined, {
        duration: 15000,
        panelClass: 'snackbar-error',
      });
    } finally {
      this.isUploading.set(false);
    }
  }

  XHR(): [XMLHttpRequest, Promise<void>] {
    const xhr = new XMLHttpRequest();
    let resolve, reject;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    xhr.addEventListener('readystatechange', ev => {
      if (xhr.readyState == XMLHttpRequest.DONE) {
        if (xhr.status >= 200 && xhr.status < 299) {
          resolve();
        } else {
          reject(
            new HttpErrorResponse({
              error: xhr.response,
              headers: new HttpHeaders(xhr.getAllResponseHeaders()),
              status: xhr.status,
              statusText: xhr.statusText,
              url: xhr.responseURL,
              redirected: false,
            }),
          );
        }
      }
    });
    return [xhr, promise];
  }

  shouldDisplayHipaaWarning(file: FileVersionResponse, dataset: DatasetResponse) {
    switch (dataset.sensitivity_level.id) {
      case SensitivityLevel.HSD:
        return false;
      case SensitivityLevel.LDS:
        return file.file_hipaa_warnings.some(w => w != 'PossibleZIPData' && w != 'PossibleDateData');
      case SensitivityLevel.DE_ID:
        return file.file_hipaa_warnings.length > 0;
      case SensitivityLevel.NA:
        return false;
    }
  }

  generateHipaaWarningTooltip(file: FileVersionResponse): string {
    const preamble = 'WARNING:\n\nThe system has detected file content that appears similar to HIPAA identifiers:\n\n';
    const ids = file.file_hipaa_warnings.map(
      warning => `  \u2022 ${warning.replace(/^Possible(.*)Data$/g, 'Possible $1 Data')}`,
    );
    const postamble = '\n\nA system admin has been notified and may audit the uploaded file.';

    return preamble + ids.join('\n') + postamble;
  }

  hipaaScanInProgress(): boolean {
    return !this.isProject() && this.files().some(fvr => fvr.is_scan_complete === false);
  }

  async openDSP() {
    if (this.isProject()) return;
    const dsp = this.dataset().associated_projects.find(p => p.is_primary_project).data_security_plan.id;
    const link = await lastValueFrom(this._download_dsp(dsp));
    globalThis.open(link.download_url, 'RDE_DSP');
  }

  _download_dsp: (id: string) => Observable<PresignedUrlResponse>;
}
